import { useState } from 'react';
import { Plus, Trash2, TriangleAlert } from 'lucide-react';
import { Button, DataState } from '@/components/ui';
import {
  useCreateSupplier,
  useDeleteSupplier,
  useSuppliers,
  useUpdateSupplier,
} from '@/lib/hooks';
import { etiquetaPunto } from '@shared/proveedores';
import { formatNumber } from '@shared/format';
import { cn } from '@/lib/cn';
import type { Proveedor } from '@shared/types';

/** Un tramo del recorrido, con sus dos extremos editables. */
function Tramo({
  rotulo,
  valor,
  onChange,
}: {
  rotulo: string;
  valor: { min: number; max: number };
  onChange: (v: { min: number; max: number }) => void;
}) {
  const campo =
    'h-8 w-16 rounded-lg border border-hairline bg-card px-2 text-right text-sm text-ink-strong ' +
    'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-ring/40';
  return (
    <div>
      <p className="text-2xs uppercase tracking-wide text-ink-faint">{rotulo}</p>
      <div className="mt-1 flex items-center gap-1.5">
        <input
          type="number"
          min={0}
          value={valor.min}
          onChange={(e) => onChange({ ...valor, min: Number(e.target.value) })}
          className={campo}
          aria-label={`${rotulo}, mínimo de días`}
        />
        <span className="text-xs text-ink-faint">a</span>
        <input
          type="number"
          min={0}
          value={valor.max}
          onChange={(e) => onChange({ ...valor, max: Number(e.target.value) })}
          className={campo}
          aria-label={`${rotulo}, máximo de días`}
        />
        <span className="text-2xs text-ink-muted">días</span>
      </div>
    </div>
  );
}

/**
 * Ficha de un proveedor: los tres tramos como rango, y el deslizador que decide en qué punto
 * del rango se para el sistema.
 *
 * El deslizador es lo que pidió Charly y es la pieza que hace útil todo esto: BET mandó los
 * tiempos como rangos («45-60 días»), y elegir el extremo **cambia la plata** — con el máximo
 * se compra antes y se inmoviliza más capital; con el mínimo se corre riesgo de quiebre. Antes
 * eso era una decisión de una sola vez, escondida en una importación; ahora es una palanca que
 * se mueve y muestra su efecto.
 */
function FichaProveedor({ p }: { p: Proveedor }) {
  const guardar = useUpdateSupplier();
  const borrar = useDeleteSupplier();
  const [borrador, setBorrador] = useState<Partial<Proveedor>>({});
  const [confirmar, setConfirmar] = useState(false);

  const v = { ...p, ...borrador };
  const rango = {
    min: v.fabricacion.min + v.transito.min + v.nacionalizacion.min,
    max: v.fabricacion.max + v.transito.max + v.nacionalizacion.max,
  };
  // El número que se ve mientras se arrastra sale de la MISMA cuenta que usa el servidor
  // (`shared/proveedores.ts`): si la pantalla hiciera la suya, podrían discrepar en un día.
  const lead = Math.round(rango.min + ((rango.max - rango.min) * v.puntoRango) / 100);
  const sucio = Object.keys(borrador).length > 0;
  const hayRango = rango.max > rango.min;

  return (
    <div className="rounded-xl border border-hairline bg-card-2 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <input
            value={v.nombre}
            onChange={(e) => setBorrador({ ...borrador, nombre: e.target.value })}
            className="w-full max-w-sm rounded-lg border border-transparent bg-transparent px-1.5 py-0.5 text-sm font-semibold text-ink-strong hover:border-hairline focus:border-primary focus:bg-card focus:outline-none"
            aria-label="Nombre del proveedor"
          />
          <p className="mt-0.5 line-clamp-2 max-w-prosa px-1.5 text-2xs leading-relaxed text-ink-faint">
            {formatNumber(p.productos)} {p.productos === 1 ? 'producto' : 'productos'}
            {p.notas && ` · ${p.notas}`}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-2xs uppercase tracking-wide text-ink-faint">Tarda</p>
          <p className="cifra text-2xl">
            {lead} <span className="text-sm font-normal text-ink-muted">días</span>
          </p>
          {hayRango && <p className="text-2xs text-ink-faint">de {rango.min} a {rango.max}</p>}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Tramo rotulo="Fabricación" valor={v.fabricacion} onChange={(x) => setBorrador({ ...borrador, fabricacion: x })} />
        <Tramo rotulo="Tráfico" valor={v.transito} onChange={(x) => setBorrador({ ...borrador, transito: x })} />
        <Tramo rotulo="Nacionalización" valor={v.nacionalizacion} onChange={(x) => setBorrador({ ...borrador, nacionalizacion: x })} />
      </div>

      {/* El deslizador sólo tiene sentido si hay rango que recorrer. */}
      {hayRango ? (
        <div className="mt-4">
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor={`punto-${p.id}`} className="text-2xs uppercase tracking-wide text-ink-faint">
              Con cuánto margen calcular
            </label>
            <span className="text-xs text-ink-muted">
              {etiquetaPunto(v.puntoRango)} — <span className="font-semibold text-ink-strong">{lead} días</span>
            </span>
          </div>
          <input
            id={`punto-${p.id}`}
            type="range"
            min={0}
            max={100}
            step={5}
            value={v.puntoRango}
            onChange={(e) => setBorrador({ ...borrador, puntoRango: Number(e.target.value) })}
            className="mt-2 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-hover accent-primary"
          />
          <div className="mt-1 flex justify-between text-2xs text-ink-faint">
            <span>{rango.min} d · optimista</span>
            <span>{rango.max} d · conservador</span>
          </div>
        </div>
      ) : (
        <p className="mt-3 text-2xs text-ink-faint">
          Este proveedor tiene un tiempo firme, sin rango: no hay margen que elegir.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            disabled={!sucio || guardar.isPending}
            onClick={() =>
              guardar.mutate(
                { id: p.id, patch: borrador as Record<string, unknown> },
                { onSuccess: () => setBorrador({}) },
              )
            }
          >
            {guardar.isPending ? 'Guardando…' : `Guardar${p.productos ? ` y recalcular ${p.productos}` : ''}`}
          </Button>
          {sucio && (
            <Button variant="ghost" onClick={() => setBorrador({})}>
              Descartar
            </Button>
          )}
        </div>
        {confirmar ? (
          <span className="flex items-center gap-2 text-xs text-ink-muted">
            ¿Borrar {p.nombre}?
            <Button variant="outline" onClick={() => borrar.mutate(p.id)}>
              Sí, borrar
            </Button>
            <Button variant="ghost" onClick={() => setConfirmar(false)}>
              No
            </Button>
          </span>
        ) : (
          <Button variant="ghost" icon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => setConfirmar(true)}>
            Borrar
          </Button>
        )}
      </div>

      {guardar.isError && (
        <p className="mt-2 text-xs text-danger-text">{(guardar.error as Error).message}</p>
      )}
      {/* Se dice cuántos productos se recalcularon: un guardado que mueve 220 fechas de compra
          no puede pasar en silencio. */}
      {guardar.isSuccess && !sucio && guardar.data?.productosActualizados ? (
        <p className="mt-2 text-xs text-success-text">
          Guardado · se recalcularon {formatNumber(guardar.data.productosActualizados)} productos
        </p>
      ) : null}
    </div>
  );
}

export function Proveedores() {
  const q = useSuppliers();
  const crear = useCreateSupplier();

  return (
    <DataState query={q}>
      {(d) => (
        <div className="space-y-4 p-5">
          <p className="max-w-prosa text-xs leading-relaxed text-ink-muted">
            El tiempo de espera es del <strong className="text-ink-strong">proveedor</strong>, no
            de cada producto: se carga una vez acá y vale para todos los que tenga asignados. De
            este dato salen <strong className="text-ink-strong">cuándo</strong> hay que pedir y{' '}
            <strong className="text-ink-strong">cuánto</strong> comprar.
          </p>
          {/* Lo que está en juego con el deslizador, dicho UNA vez para toda la sección: en
              cada ficha se repetía tantas veces como proveedores hubiera. */}
          <p className="max-w-prosa text-xs leading-relaxed text-ink-muted">
            Cuando el tiempo viene como un rango («45 a 60 días»), el deslizador decide con
            cuánto margen calcular. <strong className="text-ink-strong">Más a la derecha</strong>{' '}
            el sistema asume que el proveedor tarda lo máximo: compra antes y deja más capital
            inmovilizado. <strong className="text-ink-strong">Más a la izquierda</strong> compra
            más tarde, con menos plata quieta y más riesgo de quedarse sin stock.
          </p>

          {d.sinProveedor > 0 && (
            <div className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning-soft/50 p-3">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning-text" />
              <p className="max-w-prosa text-xs leading-relaxed text-ink">
                <strong className="text-ink-strong">
                  {formatNumber(d.sinProveedor)} productos siguen sin proveedor
                </strong>{' '}
                y calculan con un supuesto de 30 días. Si alguno se fabrica afuera, su fecha de
                compra y su cantidad están mal: se asignan desde Configuración por Producto.
              </p>
            </div>
          )}

          {d.items.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-muted">
              Todavía no hay proveedores cargados. Creá uno y asignale sus productos.
            </p>
          ) : (
            <div className="space-y-3">
              {d.items.map((p) => (
                <FichaProveedor key={p.id} p={p} />
              ))}
            </div>
          )}

          <Button
            variant="outline"
            size="md"
            icon={<Plus className="h-4 w-4" />}
            disabled={crear.isPending}
            onClick={() =>
              crear.mutate({
                nombre: `Proveedor ${d.items.length + 1}`,
                fabricacion: { min: 30, max: 30 },
                transito: { min: 0, max: 0 },
                nacionalizacion: { min: 0, max: 0 },
                puntoRango: 50,
              })
            }
            className={cn(crear.isPending && 'opacity-60')}
          >
            {crear.isPending ? 'Creando…' : 'Agregar un proveedor'}
          </Button>
          {crear.isError && <p className="text-xs text-danger-text">{(crear.error as Error).message}</p>}
        </div>
      )}
    </DataState>
  );
}
